import { type HTMLInputTypeAttribute } from 'react'
import { Text, View } from 'react-native'
import { useController, type Control, type FieldPath, type FieldValues, type RegisterOptions } from 'react-hook-form'

import { cn } from '@/utils/cn'
import { Input, type InputProps } from '@/components/base'

interface TextareaProps<TValues extends FieldValues, TPath extends FieldPath<TValues>>
  extends Omit<InputProps, 'defaultValue' | 'error' | 'onChange' | 'value'> {
  label?: string
  hint?: string
  type?: HTMLInputTypeAttribute
  control: Control<TValues>
  name: TPath
  rules?: RegisterOptions<TValues, TPath>
  hasPlaceholder?: boolean
}

export function Textarea<TValues extends FieldValues, TPath extends FieldPath<TValues>>({
  label,
  hint,
  type,
  control,
  name,
  rules,
  hasPlaceholder = true,
  ...props
}: TextareaProps<TValues, TPath>) {
  const {
    field: { ref, value, onChange },
    fieldState: { error }
  } = useController({ control, name, rules })
  const hasError = error !== undefined

  return (
    <View className={'gap-y-1'}>
      <View className={'flex-row items-baseline justify-between'}>
        {label && <Text className={'text-sm font-medium text-text-secondary'}>{label}</Text>}
      </View>
      <View>
        <Input
          {...props}
          testID={props.testID ?? `input-${String(name).replaceAll('.', '-')}`}
          accessibilityLabel={props.accessibilityLabel ?? label ?? props.placeholder}
          ref={ref}
          value={value}
          onChange={onChange}
          error={hasError}
          isTextarea={true}
          multiline={true}
          maxLength={800}
        />
        <View className={'absolute bottom-3 right-3'} pointerEvents={'none'}>
          <Text
            className={cn('font-roboto text-sm text-text-tertiary', hasError && 'text-danger')}
          >{`${value.length}/800`}</Text>
        </View>
      </View>
      {!hasError && hint && <Text className={'text-xs font-medium text-text-tertiary'}>{hint}</Text>}
      {hasError && <Text className={'text-xs font-medium text-text-danger'}>{error.message}</Text>}
      {hasPlaceholder && !hasError && !hint && <View className={'h-4'} />}
    </View>
  )
}
